import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck, ChevronRight, ChevronLeft, ChevronDown, Inbox, Undo2, Clock, CheckCircle2, Minus, Plus,
  Trash2, Info, Send, Save, UserCheck, AlertTriangle, RotateCcw, XCircle, Truck, Printer, MessageCircle, FileDown,
} from 'lucide-react';
import { orderReviewAPI, brandingAPI, medicineOrderAPI } from '../../services/api';
import { useToast, useConfirm, usePrompt } from '../UIProvider';
import Select from '../Select';
import { formatClinicDate, formatClinicDateTime } from '../../utils/clinicTime';
import { scrollContentToTop } from '../../utils/scroll';
import { printOrderSheet } from './orderSheet';
import { shareOrderOnWhatsApp } from './orderShare';
import { saveOrderPdf } from './orderPdf';
import usePdfDownload from '../../hooks/usePdfDownload';
import { FEATURES } from '../../config/features';
import ShareForReviewDialog from './ShareForReviewDialog';
import { packCount, packKind, packWord, unitsIn } from './packWords';

// Order Reviews: an order checked by a colleague before it goes to the supplier.
//
//   * The person who raised an order shares it from the Order Board with one colleague.
//   * The colleague opens it here, changes packs, takes items off or adds them, or moves an item to
//     another supplier, and says why on every line they change. Then they send it back. An item
//     taken off is asked why: enough in stock (with the days to leave it off orders), discontinued,
//     or something else in their words. The first two are reason enough on their own.
//   * It comes back here to the person who raised it, and only to them. They take or keep each
//     change, then accept and send it (the order sheet opens to print), or share it again. A move
//     taken puts the item on that supplier's draft, the next order the board raises for them. A
//     discontinuation taken marks the item discontinued, and the Order Board leaves it out until it
//     is resumed on the Medicines page. A deferral taken leaves it off the Order Board until the
//     day, or until less than 5% of the stock there was when it was taken is left. With nothing
//     left to send but something deferred, discontinued or moved, accepting closes the order
//     unsent and still does those.
//   * A review accepted and sent can print its order sheet again, as the order stands now, for
//     anyone who may see Medicine Orders and is not a reviewer (the order carries prices).
//
// The reviewer never sees a price or what is left on the shelf: the server leaves both out of
// everything it sends a reviewer (`you.seesPrices` false, no `onHand`, the planner's sentence
// without its prices and stock figures), and this page shows neither unless it is there.
//
// The page takes the order from the address, #/order-reviews/<order id>, so a link or a refresh
// opens the same review. Who may do what is the server's call; the page shows what `you` says.

const PAGE = 'order-reviews';

const rs = (n) => (n == null ? '' : `Rs ${Math.round(Number(n)).toLocaleString()}`);
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const when = (v) => formatClinicDate(String(v || '').slice(0, 10), { day: 'numeric', month: 'short', year: undefined }, '');

/** The order open from the address, or null for the list. */
const orderIdFromHash = () => {
  const parts = window.location.hash.replace(/^#\/?/, '').split('/');
  return parts[0] === PAGE && /^[\w-]+$/.test(parts[1] || '') ? parts[1] : null;
};

/** NONE, MORE, FEWER, ADDED, REMOVED or MOVED: the same rule the server applies. */
export const changeOf = (line) => {
  const p = Number(line.proposedPacks) || 0;
  if (line.originalPacks == null) return p > 0 ? 'ADDED' : 'NONE';
  if (p === 0) return 'REMOVED';
  if (line.moveToVendorId) return 'MOVED';
  if (p > line.originalPacks) return 'MORE';
  if (p < line.originalPacks) return 'FEWER';
  return 'NONE';
};

/** Taken off by the reviewer as discontinued: only a line of the order taken off can be. */
export const saysDiscontinued = (line) => !!line.discontinued && changeOf(line) === 'REMOVED';

/**
 * Taken off by the reviewer for enough in stock, to leave off orders for `deferDays`. While the
 * reviewer is still answering, their answer (`offWhy`) says so before the days are typed in.
 */
export const saysEnoughStock = (line) => changeOf(line) === 'REMOVED'
  && (line.offWhy ? line.offWhy === 'ENOUGH' : !!line.deferDays && !line.discontinued);

/** The days a reviewer can leave an item off orders for, besides a number of their own. */
const DEFER_DAYS = [15, 30, 60, 90];
const MAX_DEFER_DAYS = 365;
const validDeferDays = (d) => Number.isInteger(Number(d)) && Number(d) >= 1 && Number(d) <= MAX_DEFER_DAYS;

const CHANGE = {
  MORE: { label: 'More', chip: 'bg-amber-100 text-amber-800', bar: 'border-l-amber-400' },
  FEWER: { label: 'Fewer', chip: 'bg-amber-100 text-amber-800', bar: 'border-l-amber-400' },
  ADDED: { label: 'Added', chip: 'bg-emerald-100 text-emerald-800', bar: 'border-l-emerald-500' },
  REMOVED: { label: 'Taken off', chip: 'bg-red-100 text-red-700', bar: 'border-l-red-400' },
  MOVED: { label: 'Other supplier', chip: 'bg-sky-100 text-sky-800', bar: 'border-l-sky-400' },
  NONE: { label: '', chip: '', bar: 'border-l-transparent' },
};

const DISCONTINUED = { label: 'Discontinued', chip: 'bg-gray-200 text-gray-700', bar: 'border-l-red-400' };
const ENOUGH_STOCK = { label: 'Enough in stock', chip: 'bg-amber-100 text-amber-800', bar: 'border-l-red-400' };

/** How a line under change `c` looks; a line taken off as discontinued, or for enough in stock, says so. */
const lookOf = (l, c) => {
  if (c !== 'REMOVED') return CHANGE[c];
  if (saysDiscontinued(l)) return DISCONTINUED;
  return saysEnoughStock(l) ? ENOUGH_STOCK : CHANGE.REMOVED;
};

const STATE = {
  WITH_REVIEWER: { label: 'With the reviewer', chip: 'bg-violet-100 text-violet-800' },
  RETURNED: { label: 'Back from review', chip: 'bg-amber-100 text-amber-800' },
  TAKEN_BACK: { label: 'Taken back', chip: 'bg-amber-100 text-amber-800' },
  ACCEPTED: { label: 'Accepted and sent', chip: 'bg-emerald-100 text-emerald-800' },
  SHARED_AGAIN: { label: 'Shared again', chip: 'bg-gray-100 text-gray-700' },
  DISCARDED: { label: 'Dropped', chip: 'bg-red-100 text-red-700' },
  MOVED_AWAY: { label: 'Moved to other suppliers', chip: 'bg-sky-100 text-sky-800' },
  NOTHING_LEFT: { label: 'Closed, nothing to send', chip: 'bg-gray-100 text-gray-700' },
  CANCELLED: { label: 'Order cancelled', chip: 'bg-red-100 text-red-700' },
  CLOSED: { label: 'Closed', chip: 'bg-gray-100 text-gray-700' },
};

const packsText = (n, l) => packCount(n, l.supplierBottle, l.unitsPerPack);

const changeSentence = (l) => {
  // "8 packs (80)": the size of the pack is already said once, the units in the bracket are not.
  const short = (n) => `${plural(n, packWord(l.supplierBottle))}${unitsIn(n, l.supplierBottle, l.unitsPerPack)}`;
  switch (changeOf(l)) {
    case 'ADDED': return `Added: ${packsText(l.proposedPacks, l)}`;
    case 'REMOVED': return `Taken off${saysDiscontinued(l) ? ' as discontinued' : saysEnoughStock(l) ? `, enough in stock for ${plural(Number(l.deferDays), 'day')}` : ''}, was ${packsText(l.originalPacks, l)}`;
    case 'MOVED': return `From ${l.moveToVendorName} instead: ${packsText(l.proposedPacks, l)}${Number(l.proposedPacks) !== l.originalPacks ? `, raised with ${short(l.originalPacks)}` : ''}`;
    case 'MORE':
    case 'FEWER': return `${packsText(l.originalPacks, l)} to ${short(Number(l.proposedPacks))}`;
    default: return packsText(l.proposedPacks, l);
  }
};

/** The question over a changed line's reason. Discontinued or enough in stock is reason enough on its own. */
const reasonQuestion = (l, c) => (saysDiscontinued(l) || saysEnoughStock(l) ? 'Anything to add?' : {
  ADDED: 'Why add it?',
  REMOVED: 'What is the reason?',
  MORE: 'Why more?',
  FEWER: 'Why fewer?',
  MOVED: `Why order it from ${l.moveToVendorName}?`,
}[c]);

/** "Added by Adil after the order was shared": a line that joined the order while out for review. */
const addedLater = (l) => (l.addedLaterBy ? `Added by ${l.addedLaterBy} after the order was shared` : null);

/** One toast per supplier for the items a decision moved to their next order. */
const announceMoves = (notify, moved) => {
  const byOrder = new Map();
  (moved || []).forEach((m) => {
    const one = byOrder.get(m.orderNumber) || { ...m, names: [] };
    one.names.push(m.name);
    byOrder.set(m.orderNumber, one);
  });
  byOrder.forEach((m) => notify('success', m.inReview
    ? `${m.names.join(', ')} moved to ${m.vendorName}'s order ${m.orderNumber}, out for review`
    : `${m.names.join(', ')} moved to the next order from ${m.vendorName} (${m.orderNumber})`));
};

/** The items a decision marked discontinued, in one toast. */
const announceDiscontinued = (notify, names) => {
  if (!names || names.length === 0) return;
  notify('success', `${names.join(', ')} marked discontinued. No order proposes ${names.length === 1 ? 'it' : 'them'} until resumed on the Medicines page.`);
};

/** The items a decision deferred, one toast each: every one has its own day. */
const announceDeferred = (notify, deferred) => (deferred || []).forEach((d) => notify('success',
  `${d.name} left off orders until ${when(d.until)}${d.floor > 0 ? `, or until fewer than ${d.floor} are in stock` : ''}.`));

// =================================================================== the page

const OrderReviewsManagement = ({ hasPermission }) => {
  const [openId, setOpenId] = useState(orderIdFromHash);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showFinished, setShowFinished] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await orderReviewAPI.mine());
    } catch (e) {
      setError(e.message || 'Could not load the reviews');
    }
  }, []);

  useEffect(() => {
    const onHash = () => setOpenId(orderIdFromHash());
    const onReset = (e) => { if (e.detail?.moduleId === PAGE) setOpenId(null); };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('clinic:module-reset', onReset);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('clinic:module-reset', onReset);
    };
  }, []);
  useEffect(() => { if (!openId) load(); }, [openId, load]);
  useEffect(() => { scrollContentToTop(); }, [openId]);

  const open = (orderId) => { window.location.hash = `/${PAGE}/${orderId}`; setOpenId(orderId); };
  const close = () => { window.location.hash = `/${PAGE}`; setOpenId(null); };
  const changed = () => window.dispatchEvent(new Event('clinic:order-reviews-changed'));
  const seesOrders = typeof hasPermission !== 'function' || hasPermission('PHARMACY_ORDERS', 'VIEW');

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center"><ClipboardCheck className="h-7 w-7 text-white" /></div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Order Reviews</h1>
              <p className="text-gray-600">Orders shared with you to check, and orders you shared coming back</p>
            </div>
          </div>
        </div>

        {openId ? (
          <ReviewDetail orderId={openId} onBack={close} onChanged={changed} seesOrders={seesOrders} />
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-red-700">{error}</div>
        ) : !data ? (
          <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        ) : (
          <ReviewLists data={data} onOpen={open} showFinished={showFinished} setShowFinished={setShowFinished} />
        )}
      </div>
    </div>
  );
};

const ReviewLists = ({ data, onOpen, showFinished, setShowFinished }) => {
  const groups = [
    ['toReview', 'For you to review', Inbox, 'Change what needs changing, say why, and send it back.'],
    ['backToMe', 'Back to you', Undo2, 'Take or keep each change, then accept and send.'],
    ['waiting', 'With a reviewer', Clock, 'Shared by you, not back yet.'],
  ];
  const open = groups.filter(([k]) => (data[k] || []).length > 0);
  const finished = data.finished || [];
  return (
    <div className="space-y-6">
      {open.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700">Nothing waiting on you</h3>
          <p className="text-gray-500 text-sm">An order shared with you for review shows here.</p>
        </div>
      )}
      {open.map(([key, title, Icon, hint]) => (
        <section key={key} aria-label={title}>
          <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Icon className="w-5 h-5 text-navy-700" /> {title} ({data[key].length})</h2>
          <p className="text-xs text-gray-500 mb-2">{hint}</p>
          <ul className="space-y-3">{data[key].map((r) => <ReviewRow key={`${r.orderId}-${r.round}`} r={r} onOpen={onOpen} />)}</ul>
        </section>
      ))}
      {finished.length > 0 && (
        <section aria-label="Finished" className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <button type="button" onClick={() => setShowFinished((v) => !v)} aria-expanded={showFinished}
            className="w-full flex items-center justify-between p-4 text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
            <span className="font-semibold text-gray-800">Finished ({finished.length})</span>
            {showFinished ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          {showFinished && <ul className="px-4 pb-4 space-y-3">{finished.map((r) => <ReviewRow key={`${r.orderId}-${r.round}`} r={r} onOpen={onOpen} />)}</ul>}
        </section>
      )}
    </div>
  );
};

const ReviewRow = ({ r, onOpen }) => {
  const st = STATE[r.status] || STATE.CLOSED;
  const who = r.reviewerIsMe
    ? `From ${r.raisedByName}, shared ${when(r.sharedAt)}`
    : `${r.raisedByMe ? 'You shared it with' : `${r.raisedByName} shared it with`} ${r.reviewerName} on ${when(r.sharedAt)}`;
  return (
    <li>
      <button type="button" onClick={() => onOpen(r.orderId)}
        className="w-full text-left bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="font-bold text-gray-900">{r.vendorName} <span className="font-normal text-gray-500 text-sm">· {r.orderNumber}{r.round > 1 ? ` · round ${r.round}` : ''}</span></p>
            <p className="text-sm text-gray-600">{who}</p>
            {(r.raisedAt || r.decidedAt) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {[r.raisedAt && `Raised ${when(r.raisedAt)}`, r.decidedAt && `Completed ${when(r.decidedAt)}`].filter(Boolean).join(' · ')}
              </p>
            )}
            {r.returnedAt && ['RETURNED', 'TAKEN_BACK'].includes(r.status) && (
              <p className="text-sm text-gray-600">{r.status === 'TAKEN_BACK' ? 'Taken back' : 'Sent back'} {when(r.returnedAt)}: {r.changes ? plural(r.changes, 'change') : 'no changes'}</p>
            )}
            {r.note && r.reviewerIsMe && r.status === 'WITH_REVIEWER' && <p className="text-sm text-navy-800 mt-1">“{r.note}”</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">{plural(r.lines, 'item')}</p>
              {r.total != null && <p className="font-bold text-gray-900">{rs(r.total)}</p>}
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${st.chip}`}>{st.label}</span>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </button>
    </li>
  );
};

// =================================================================== one order

const ReviewDetail = ({ orderId, onBack, onChanged, seesOrders }) => {
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReview(await orderReviewAPI.get(orderId));
    } catch (e) {
      setError(e.message || 'Could not open the review');
    }
  }, [orderId]);
  useEffect(() => { setReview(null); load(); }, [load]);

  const after = (next) => { setReview(next); onChanged(); };

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-medium text-navy-700 hover:underline">
        <ChevronLeft className="w-4 h-4" /> All reviews
      </button>
      {error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-red-700">{error}</div>
      ) : !review ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
      ) : review.you.canEdit ? (
        <ReviewEditor key={`${review.review.id}`} review={review} onSaved={after} onDone={() => { onChanged(); onBack(); }} />
      ) : review.you.canDecide ? (
        <ReviewDecision review={review} onDone={() => { onChanged(); onBack(); }} onChanged={after} />
      ) : (
        <ReviewReadOnly review={review} seesOrders={seesOrders} onChanged={after} onDone={() => { onChanged(); onBack(); }} />
      )}
    </div>
  );
};

/** The order, the round and the notes, above every view of it. */
const ReviewHeader = ({ review, children }) => {
  const r = review.review;
  const st = STATE[r.status] || STATE.CLOSED;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{review.vendorName}</h2>
          <p className="text-sm text-gray-600">{review.orderNumber}{r.round > 1 ? ` · round ${r.round}` : ''} · raised by {r.raisedByName}, reviewed by {r.reviewerName}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {review.raisedAt && <>Raised {formatClinicDateTime(review.raisedAt)} · </>}
            {review.raisedAt ? 'shared' : 'Shared'} {formatClinicDateTime(r.sharedAt)}
            {r.returnedAt && <> · {r.status === 'TAKEN_BACK' ? 'taken back' : 'sent back'} {formatClinicDateTime(r.returnedAt)}</>}
            {r.decidedAt && <> · completed {formatClinicDateTime(r.decidedAt)} by {r.decidedBy}</>}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${st.chip}`}>{st.label}</span>
      </div>
      {r.note && (
        <div className="mt-3 rounded-lg bg-navy-50 border border-navy-100 p-3 text-sm text-navy-900">
          <span className="font-semibold">{r.raisedByName}:</span> {r.note}
        </div>
      )}
      {r.reviewerNote && r.status !== 'WITH_REVIEWER' && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">
          <span className="font-semibold">{r.reviewerName}:</span> {r.reviewerNote}
        </div>
      )}
      {children}
    </div>
  );
};

const Why = ({ text }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs text-navy-700 hover:underline">
        <Info className="w-3.5 h-3.5" />{open ? 'Hide why' : 'Why on the order?'}
      </button>
      {open && <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm text-gray-800 leading-relaxed">{text}</div>}
    </>
  );
};

const lineFacts = (l, prices) => [
  l.onHand != null ? `In stock ${Number(l.onHand).toLocaleString()}` : null,
  packKind(l.supplierBottle, l.unitsPerPack),
  prices ? (l.packPrice != null ? `${rs(l.packPrice)} a pack` : 'no price yet') : null,
  l.suggestedPacks != null ? `planner suggested ${l.suggestedPacks}` : null,
].filter(Boolean).join(' · ');

// =================================================================== the reviewer

/** Why a line is taken off, as the reviewer's question asks it. */
const OFF_WHY = [['ENOUGH', 'Enough in stock'], ['DISCONTINUED', 'Discontinued'], ['OTHER', 'Something else']];

/** The answer a saved line already gives: none until the reviewer chooses one. */
const offWhyOf = (l) => {
  if (changeOf(l) !== 'REMOVED') return null;
  if (l.discontinued) return 'DISCONTINUED';
  if (l.deferDays) return 'ENOUGH';
  return l.reason ? 'OTHER' : null;
};

const toEditable = (review) => review.lines.map((l) => ({
  ...l, key: l.id, reason: l.reason || '', discontinued: !!l.discontinued, deferDays: l.deferDays || null,
  offWhy: offWhyOf(l), deferOwn: !!l.deferDays && !DEFER_DAYS.includes(l.deferDays),
}));

/** Nothing said yet about why a line was taken off: it has no answer. */
const NOT_OFF = { discontinued: false, deferDays: null, offWhy: null, deferOwn: false };

const ReviewEditor = ({ review, onSaved, onDone }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const r = review.review;
  const prices = review.you.seesPrices === true;
  const [lines, setLines] = useState(() => toEditable(review));
  const [note, setNote] = useState(r.reviewerNote || '');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [adding, setAdding] = useState(false);
  const [catalogue, setCatalogue] = useState(null);
  const [picking, setPicking] = useState(null);
  const [suppliers, setSuppliers] = useState({});

  const set = (key, patch) => { setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l))); setDirty(true); };
  const drop = (key) => { setLines((ls) => ls.filter((l) => l.key !== key)); setDirty(true); };

  const changes = lines.filter((l) => changeOf(l) !== 'NONE');
  // A line taken off needs its answer, and words only for "something else"; any other change needs words.
  const missing = changes.filter((l) => (changeOf(l) === 'REMOVED'
    ? !l.offWhy || (l.offWhy === 'OTHER' && !l.reason.trim())
    : !l.reason.trim()));
  const blank = lines.filter((l) => l.proposedPacks === '');
  const badDays = lines.filter((l) => saysEnoughStock(l) && !validDeferDays(l.deferDays));
  const before = lines.reduce((s, l) => s + (l.originalPacks != null && l.packPrice != null ? l.originalPacks * l.packPrice : 0), 0);
  const after = lines.reduce((s, l) => s + (l.packPrice != null && changeOf(l) !== 'MOVED' ? (Number(l.proposedPacks) || 0) * l.packPrice : 0), 0);
  const staying = lines.filter((l) => (Number(l.proposedPacks) || 0) > 0 && changeOf(l) !== 'MOVED').length;
  const moving = lines.filter((l) => changeOf(l) === 'MOVED').length;

  const payload = () => lines.map((l) => ({
    lineId: l.id || null, medicineId: l.medicineId, packs: Number(l.proposedPacks) || 0, reason: l.reason, vendorId: l.moveToVendorId || null,
    discontinued: saysDiscontinued(l), deferDays: saysEnoughStock(l) ? Number(l.deferDays) : null,
  }));

  const chooseWhy = (l, why) => {
    set(l.key, why === 'ENOUGH'
      ? { offWhy: why, discontinued: false, deferDays: l.deferDays || 30 }
      : { offWhy: why, discontinued: why === 'DISCONTINUED', deferDays: null, deferOwn: false });
    if (why === 'OTHER') setTimeout(() => document.getElementById(`reason-${l.key}`)?.focus(), 0);
  };

  /** Packs and days that cannot be sent yet, said once; true when there are none. */
  const readyToSave = () => {
    if (blank.length) { notify('warning', `Enter the packs for ${blank[0].name}.`); return false; }
    if (badDays.length) {
      notify('warning', `Enter the days to leave ${badDays[0].name} off orders, 1 to ${MAX_DEFER_DAYS}.`);
      document.getElementById(`days-${badDays[0].key}`)?.focus();
      return false;
    }
    return true;
  };

  // Another supplier for a line: who has delivered it comes first, and no price is ever shown.
  const startPicking = async (l) => {
    setPicking(l.key);
    if (suppliers[l.medicineId]) return;
    try {
      const list = await orderReviewAPI.suppliers(review.orderId, l.medicineId);
      setSuppliers((s) => ({ ...s, [l.medicineId]: list }));
    } catch (e) { notify('error', e.message || 'Could not load the suppliers'); setPicking(null); }
  };
  const moveLine = (l, vendorId) => {
    setPicking(null);
    const s = (suppliers[l.medicineId] || []).find((x) => x.vendorId === vendorId);
    if (!s) return;
    if (s.thisOrder) { set(l.key, { moveToVendorId: null, moveToVendorName: null }); return; }
    set(l.key, { moveToVendorId: s.vendorId, moveToVendorName: s.vendorName });
    setTimeout(() => document.getElementById(`reason-${l.key}`)?.focus(), 0);
  };
  const supplierOptions = (l) => (suppliers[l.medicineId] || []).map((s) => ({
    value: s.vendorId,
    label: `${s.vendorName} · ${s.thisOrder ? 'this order' : s.lastDelivered ? `delivered it ${when(s.lastDelivered)}` : 'has not delivered it'}`,
  }));

  const save = async () => {
    if (!readyToSave()) return;
    setBusy(true);
    try {
      const next = await orderReviewAPI.save(review.orderId, payload(), note);
      setLines(toEditable(next));
      setDirty(false);
      onSaved(next);
      notify('success', 'Saved. It stays with you until you send it back.');
    } catch (e) { notify('error', e.message || 'Could not save'); }
    finally { setBusy(false); }
  };

  const sendBack = async () => {
    if (!readyToSave()) return;
    if (missing.length) {
      setShowErrors(true);
      notify('warning', `Say why you changed ${missing.map((l) => l.name).join(', ')}.`);
      const first = missing[0];
      document.getElementById(changeOf(first) === 'REMOVED' && !first.offWhy ? `why-${first.key}` : `reason-${first.key}`)?.focus();
      return;
    }
    const ok = await confirm({
      title: `Send it back to ${r.raisedByName}?`,
      message: changes.length
        ? `${plural(changes.length, 'change')}. ${r.raisedByName} takes or keeps each one, then sends the order. You cannot change it after this.`
        : `No changes: you are saying the order is right as it is. ${r.raisedByName} sends it from here.`,
      confirmText: 'Send back',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await orderReviewAPI.sendBack(review.orderId, payload(), note);
      notify('success', `${review.orderNumber} sent back to ${r.raisedByName}`);
      onDone();
    } catch (e) { notify('error', e.message || 'Could not send it back'); }
    finally { setBusy(false); }
  };

  const startAdding = async () => {
    setAdding(true);
    if (catalogue) return;
    try { setCatalogue(await orderReviewAPI.catalogue(review.orderId)); }
    catch (e) { notify('error', e.message || 'Could not load the items'); setAdding(false); }
  };

  const add = async (medicineId) => {
    if (!medicineId) return;
    setAdding(false);
    if (lines.some((l) => l.medicineId === medicineId)) { notify('info', 'That item is already on this order.'); return; }
    try {
      const p = await orderReviewAPI.preview(review.orderId, medicineId);
      const key = `new-${medicineId}`;
      setLines((ls) => [...ls, {
        key, id: null, medicineId, name: p.name, code: p.code, vendorItemName: p.vendorItemName, supplierBottle: p.supplierBottle,
        unitsPerPack: p.unitsPerPack, packPrice: p.packPrice, originalPacks: null, proposedPacks: p.suggestedPacks || 1,
        suggestedPacks: p.suggestedPacks, explanation: p.explanation, reason: '',
      }]);
      setDirty(true);
      setTimeout(() => document.getElementById(`reason-${key}`)?.focus(), 0);
    } catch (e) { notify('error', e.message || 'Could not add it'); }
  };

  const options = (catalogue || [])
    .filter((m) => !lines.some((l) => l.medicineId === m.id))
    .map((m) => ({ value: m.id, label: `${m.name}${m.code ? ` · ${m.code}` : ''}` }));

  return (
    <div className="space-y-4">
      <ReviewHeader review={review}>
        <p className="mt-3 text-sm text-gray-700">
          Change the packs, take items off or add them, or order an item from another supplier, and say why on every
          line you change. For an item you take off, say if there is enough in stock (and for how long) or it is discontinued.
          Nothing goes to any supplier until {r.raisedByName} accepts.
        </p>
      </ReviewHeader>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <ul className="divide-y divide-gray-100">
          {lines.map((l) => {
            const c = changeOf(l);
            const look = lookOf(l, c);
            const removed = c === 'REMOVED';
            const packs = l.proposedPacks === '' ? '' : Number(l.proposedPacks) || 0;
            // A line taken off asks why first; the words come after the answer.
            const needsReason = c !== 'NONE' && (!removed || !!l.offWhy);
            const reasonRequired = needsReason && !saysDiscontinued(l) && !saysEnoughStock(l);
            const reasonMissing = showErrors && reasonRequired && !l.reason.trim();
            const whyMissing = showErrors && removed && !l.offWhy;
            const daysBad = showErrors && saysEnoughStock(l) && !validDeferDays(l.deferDays);
            return (
              <li key={l.key} className={`border-l-4 ${look.bar} ${removed ? 'bg-gray-50' : 'bg-white'}`}>
                <div className="flex items-start gap-3 px-4 py-3 flex-wrap sm:flex-nowrap">
                  <div className="min-w-0 w-full sm:w-auto sm:flex-1">
                    <p className="font-semibold">
                      <span className={removed ? 'text-gray-500 line-through' : 'text-gray-900'}>{l.name}</span>
                      {look.label && <span className={`ml-2 align-middle inline-block px-2 py-0.5 rounded-full text-xs font-medium ${look.chip}`}>{look.label}</span>}
                    </p>
                    {l.vendorItemName && l.vendorItemName !== l.name && <p className="text-xs text-gray-500">{l.vendorItemName}</p>}
                    {addedLater(l) && <p className="text-xs text-violet-800 mt-0.5">{addedLater(l)}</p>}
                    <p className="text-xs text-gray-500 mt-0.5">{lineFacts(l, prices)}</p>
                    {l.originalPacks != null && c !== 'NONE' && !(c === 'MOVED' && Number(l.proposedPacks) === l.originalPacks) && (
                      <p className="text-xs text-amber-800 mt-0.5">Raised with {packsText(l.originalPacks, l)}</p>
                    )}
                    {!removed && l.originalPacks != null && (
                      <div className="mt-1.5 text-xs">
                        {picking === l.key ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="w-80 max-w-full">
                              {suppliers[l.medicineId]
                                ? <Select value={l.moveToVendorId || review.vendorId} onChange={(v) => moveLine(l, v)} placeholder="Choose the supplier" options={supplierOptions(l)} buttonClassName="p-2 rounded-lg text-sm" />
                                : <span className="text-gray-500">Loading the suppliers…</span>}
                            </div>
                            <button type="button" onClick={() => setPicking(null)} className="text-sm text-gray-600 hover:underline">Cancel</button>
                          </div>
                        ) : c === 'MOVED' ? (
                          <p className="text-sky-800 flex items-center gap-1.5 flex-wrap">
                            <Truck className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>Order from <span className="font-semibold">{l.moveToVendorName}</span> instead, on their next order</span>
                            <button type="button" onClick={() => startPicking(l)} aria-label={`Choose another supplier for ${l.name}`} className="text-navy-700 hover:underline">Change</button>
                            <button type="button" onClick={() => set(l.key, { moveToVendorId: null, moveToVendorName: null })} className="text-navy-700 hover:underline">Keep with {review.vendorName}</button>
                          </p>
                        ) : (
                          <button type="button" onClick={() => startPicking(l)} aria-label={`Order ${l.name} from another supplier`}
                            className="inline-flex items-center gap-1 text-navy-700 hover:underline">
                            <Truck className="w-3.5 h-3.5" aria-hidden="true" /> Order from another supplier
                          </button>
                        )}
                      </div>
                    )}
                    <div className="mt-1"><Why text={l.explanation} /></div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                    {removed ? (
                      <button type="button" onClick={() => set(l.key, { proposedPacks: l.originalPacks, ...NOT_OFF })}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-800 hover:bg-gray-50">
                        <RotateCcw className="w-4 h-4" /> Put it back
                      </button>
                    ) : (
                      <>
                        <div className="flex items-center gap-1">
                          <button type="button" aria-label={`One pack fewer of ${l.name}`} disabled={packs <= 1}
                            onClick={() => set(l.key, { proposedPacks: Math.max(1, packs - 1) })}
                            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"><Minus className="w-4 h-4" /></button>
                          <input type="number" min="1" inputMode="numeric" aria-label={`Packs of ${l.name}`} value={l.proposedPacks}
                            onChange={(e) => set(l.key, { proposedPacks: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            className="w-16 h-8 border border-gray-200 rounded-lg text-center font-semibold" />
                          <button type="button" aria-label={`One pack more of ${l.name}`}
                            onClick={() => set(l.key, { proposedPacks: packs + 1 })}
                            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"><Plus className="w-4 h-4" /></button>
                          {unitsIn(packs, l.supplierBottle, l.unitsPerPack) && (
                            <span className="ml-1 text-xs text-gray-500">{unitsIn(packs, l.supplierBottle, l.unitsPerPack).trim()}</span>
                          )}
                        </div>
                        <button type="button" aria-label={l.originalPacks == null ? `Remove ${l.name}` : `Take ${l.name} off the order`}
                          title={l.originalPacks == null ? 'Remove the line you added' : 'Take it off the order'}
                          onClick={() => (l.originalPacks == null ? drop(l.key) : set(l.key, { proposedPacks: 0, moveToVendorId: null, moveToVendorName: null, ...NOT_OFF }))}
                          className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                    {prices && <p className="w-24 text-right text-sm font-semibold text-gray-900">{l.packPrice != null ? rs((Number(l.proposedPacks) || 0) * l.packPrice) : ''}</p>}
                  </div>
                </div>
                {removed && (
                  <div className="px-4 pb-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span id={`why-label-${l.key}`} className="text-xs font-medium text-gray-700">Why take it off? <span className="text-red-500">*</span></span>
                      <div role="radiogroup" aria-labelledby={`why-label-${l.key}`} aria-invalid={whyMissing || undefined}
                        className={`flex gap-1 rounded-xl p-1 flex-wrap ${whyMissing ? 'bg-red-50 ring-1 ring-red-300' : 'bg-gray-100'}`}>
                        {OFF_WHY.map(([value, label], i) => (
                          <button key={value} id={i === 0 ? `why-${l.key}` : undefined} type="button" role="radio" aria-checked={l.offWhy === value}
                            onClick={() => chooseWhy(l, value)}
                            className={`px-3 py-1 rounded-lg text-xs font-medium ${l.offWhy === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {whyMissing && <p className="text-xs text-red-700 mt-1">Choose why, so {r.raisedByName} can decide.</p>}
                    {l.offWhy === 'ENOUGH' && (
                      <>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <span id={`days-label-${l.key}`} className="text-xs font-medium text-gray-700">Leave it off orders for</span>
                          <div role="radiogroup" aria-labelledby={`days-label-${l.key}`} className="flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
                            {DEFER_DAYS.map((d) => {
                              const on = !l.deferOwn && Number(l.deferDays) === d;
                              return (
                                <button key={d} type="button" role="radio" aria-checked={on}
                                  onClick={() => set(l.key, { deferDays: d, deferOwn: false })}
                                  className={`px-3 py-1 rounded-lg text-xs font-medium ${on ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}>
                                  {d} days
                                </button>
                              );
                            })}
                            <button type="button" role="radio" aria-checked={!!l.deferOwn}
                              onClick={() => { set(l.key, { deferOwn: true }); setTimeout(() => document.getElementById(`days-${l.key}`)?.focus(), 0); }}
                              className={`px-3 py-1 rounded-lg text-xs font-medium ${l.deferOwn ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}>
                              Other
                            </button>
                          </div>
                          {l.deferOwn && (
                            <span className="flex items-center gap-1.5">
                              <input id={`days-${l.key}`} type="number" min="1" max={MAX_DEFER_DAYS} inputMode="numeric" aria-label={`Days to leave ${l.name} off orders`}
                                value={l.deferDays ?? ''} aria-invalid={daysBad || undefined}
                                // The days chosen before stay in the box, selected, so typing replaces them.
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => set(l.key, { deferDays: e.target.value === '' ? '' : parseInt(e.target.value, 10) || '' })}
                                className={`w-20 h-8 border rounded-lg text-center text-sm ${daysBad ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} />
                              <span className="text-xs text-gray-600">days</span>
                            </span>
                          )}
                        </div>
                        {daysBad && <p className="text-xs text-red-700 mt-1">Enter 1 to {MAX_DEFER_DAYS} days.</p>}
                        <p className="text-xs text-gray-600 mt-1">
                          If {r.raisedByName} agrees, no order proposes it for {validDeferDays(l.deferDays) ? plural(Number(l.deferDays), 'day') : 'the days you choose'}, unless its stock runs low first.
                        </p>
                      </>
                    )}
                    {l.offWhy === 'DISCONTINUED' && (
                      <p className="text-xs text-gray-600 mt-1">If {r.raisedByName} agrees, it is marked discontinued and no order proposes it again until it is resumed.</p>
                    )}
                  </div>
                )}
                {needsReason && (
                  <div className="px-4 pb-3">
                    <label htmlFor={`reason-${l.key}`} className="block text-xs font-medium text-gray-700 mb-1">
                      {reasonQuestion(l, c)}{reasonRequired && <> <span className="text-red-500">*</span></>}
                    </label>
                    <input id={`reason-${l.key}`} type="text" maxLength={1000} value={l.reason}
                      onChange={(e) => set(l.key, { reason: e.target.value })}
                      aria-invalid={reasonMissing || undefined}
                      placeholder={saysDiscontinued(l) ? 'e.g. The company has stopped making it' : saysEnoughStock(l) ? 'e.g. Two cartons in the store room' : c === 'REMOVED' ? 'e.g. Bought from the market this week' : c === 'MOVED' ? `e.g. ${review.vendorName} has been short of it for two weeks` : 'e.g. Two doctors prescribe it now'}
                      className={`w-full border rounded-lg p-2 text-sm ${reasonMissing ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} />
                    {reasonMissing && <p className="text-xs text-red-700 mt-1">Say why, so {r.raisedByName} can decide.</p>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="px-5 py-3 border-t border-gray-100">
          {adding ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-96 max-w-full">
                {catalogue
                  ? <Select value="" onChange={add} placeholder="Choose an item to add" options={options} buttonClassName="p-2 rounded-lg text-sm" />
                  : <p className="text-sm text-gray-500">Loading the items…</p>}
              </div>
              <button type="button" onClick={() => setAdding(false)} className="text-sm text-gray-600 hover:underline">Done</button>
            </div>
          ) : (
            <button type="button" onClick={startAdding} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:underline">
              <Plus className="w-4 h-4" /> Add an item
            </button>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100">
          <label htmlFor="reviewer-note" className="block text-sm font-medium text-gray-700 mb-1">A word for {r.raisedByName} about the whole order</label>
          <textarea id="reviewer-note" rows={2} maxLength={2000} value={note} onChange={(e) => { setNote(e.target.value); setDirty(true); }}
            className="w-full border border-gray-200 rounded-lg p-2.5 text-sm" />
        </div>

        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 rounded-b-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-gray-600">{changes.length ? plural(changes.length, 'change') : 'No changes yet'}{dirty ? ' · not saved' : ''}</p>
            {prices ? (
              <p className="text-lg font-bold text-gray-900">{rs(before)}{Math.round(after) !== Math.round(before) && <> <span className="text-gray-400 font-normal">to</span> {rs(after)}</>}</p>
            ) : (
              <p className="text-lg font-bold text-gray-900">{plural(staying, 'item')} on the order{moving ? <span className="text-sm font-normal text-gray-600"> · {moving} to other suppliers</span> : null}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={save} disabled={busy || !dirty}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
              <Save className="w-4 h-4" /> Save for later
            </button>
            <button type="button" onClick={sendBack} disabled={busy}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
              <Send className="w-4 h-4" /> Send back to {r.raisedByName}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================== the person who raised it

const CHOICES = {
  DISCONTINUED: [['DISCONTINUE', 'Discontinue it'], ['TAKEN', 'Only take it off'], ['KEPT', 'Keep it']],
  ADDED: [['TAKEN', 'Add it'], ['KEPT', 'Leave it off']],
  REMOVED: [['TAKEN', 'Take it off'], ['KEPT', 'Keep it']],
  MORE: [['TAKEN', 'Take theirs'], ['KEPT', 'Keep mine']],
  FEWER: [['TAKEN', 'Take theirs'], ['KEPT', 'Keep mine']],
  MOVED: [['TAKEN', 'Move it'], ['KEPT', 'Keep it here']],
};

/** The choices on a changed line, and the one it starts on: the change as the reviewer left it. */
const choicesOf = (l, c) => {
  if (saysDiscontinued(l)) return CHOICES.DISCONTINUED;
  if (saysEnoughStock(l)) return [['DEFER', `Defer ${plural(Number(l.deferDays), 'day')}`], ['TAKEN', 'Only take it off'], ['KEPT', 'Keep it']];
  return CHOICES[c];
};
const firstChoice = (l) => {
  if (saysDiscontinued(l)) return 'DISCONTINUE';
  return saysEnoughStock(l) ? 'DEFER' : 'TAKEN';
};

/**
 * What a deferral taken now does, for the person who raised the order: the day, and the stock that
 * brings the item back sooner (5% of what is in date now). With none in stock nothing can.
 */
const deferSentence = (l) => {
  const until = l.deferUntil ? `until ${when(l.deferUntil)}` : `for ${plural(Number(l.deferDays), 'day')}`;
  if (l.deferOnHand == null) return `Left off orders ${until}, unless its stock runs low first.`;
  if (Number(l.deferOnHand) <= 0) return `Left off orders ${until}. None are in stock now, so nothing brings it back sooner.`;
  const now = Number(l.deferOnHand).toLocaleString();
  const there = Number(l.deferOnHand) === Number(l.onHand) ? `${now} in stock now` : `${now} in stock and in date now`;
  return `Left off orders ${until}, or until fewer than ${Number(l.deferFloor).toLocaleString()} are in stock (5% of the ${there}).`;
};

/** Packs the line ends with on this order under a decision: a move taken leaves none here. */
const finalPacks = (l, choice) => {
  if (choice === 'KEPT') return l.originalPacks ?? 0;
  return changeOf(l) === 'MOVED' ? 0 : Number(l.proposedPacks) || 0;
};

/** What a pack costs at the supplier a line would move to, against this order's. */
const priceThere = (l) => (l.movePackPrice != null
  ? `${rs(l.movePackPrice)} a pack from ${l.moveToVendorName} on their last bill${l.packPrice != null ? `, ${rs(l.packPrice)} here` : ''}`
  : `No bill from ${l.moveToVendorName} for it yet, so no price of theirs`);

/**
 * What became of one line in this round, opened from its row: what was raised, what the reviewer
 * did and why, and what was decided. `hidden` keeps a round still with its reviewer to the order as
 * raised. A reviewer's copy carries no prices or stock, so none shows here either.
 */
const LineStory = ({ l, review, hidden }) => {
  const r = review.review;
  const c = hidden ? 'NONE' : changeOf(l);
  const seesPrices = review.you.seesPrices !== false;
  const priced = seesPrices && l.packPrice != null;
  const cost = (n) => rs((Number(n) || 0) * l.packPrice);
  const decided = {
    TAKEN: l.movedToOrderNumber ? `${r.decidedBy} took it: on ${l.movedToOrderNumber} for ${l.moveToVendorName}`
      : l.markedDiscontinued ? `${r.decidedBy} took it off and marked it discontinued`
        : saysDiscontinued(l) ? `${r.decidedBy} took it off this order only, not as discontinued`
          : l.deferredUntil ? `${r.decidedBy} took it off and left it off orders until ${when(l.deferredUntil)}`
            : saysEnoughStock(l) ? `${r.decidedBy} took it off this order only, not deferred`
              : `${r.decidedBy} took the change`,
    KEPT: `${r.decidedBy} kept it as raised`,
  }[l.decision];
  const rows = [
    ['Raised', l.originalPacks == null ? 'Not on the order as raised' : packsText(l.originalPacks, l)],
    addedLater(l) ? ['Added', addedLater(l)] : null,
    hidden ? null : [r.reviewerName, c === 'NONE' ? 'Kept it as raised' : changeSentence(l)],
    !hidden && c !== 'NONE' && l.reason ? ['Their reason', `“${l.reason}”`] : null,
    !hidden && c === 'MOVED' && seesPrices ? ['Price', priceThere(l)] : null,
    !hidden && priced && ['MORE', 'FEWER'].includes(c) ? ['Cost', `${cost(l.originalPacks)} to ${cost(l.proposedPacks)}`] : null,
    !hidden && priced && c === 'ADDED' ? ['Cost', `${cost(l.proposedPacks)} added`] : null,
    !hidden && priced && c === 'REMOVED' ? ['Cost', `${cost(l.originalPacks)} off the order`] : null,
    !hidden && decided ? ['Decided', decided] : null,
    ['Item', lineFacts(l, seesPrices)],
  ].filter(Boolean);
  return (
    <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {rows.map(([k, v], i) => (
          <React.Fragment key={i}>
            <dt className="text-gray-500">{k}</dt>
            <dd className="text-gray-800 min-w-0">{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      {l.explanation && (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-500">Why on the order</p>
          <p className="text-gray-800 leading-relaxed">{l.explanation}</p>
        </div>
      )}
    </div>
  );
};

/** Rows that open to their story: the ids open, and a toggle. */
const useOpenRows = () => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen((was) => {
    const next = new Set(was);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return [open, toggle];
};

const ReviewDecision = ({ review, onDone, onChanged }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const r = review.review;
  const changed = review.lines.filter((l) => changeOf(l) !== 'NONE');
  const unchanged = review.lines.filter((l) => changeOf(l) === 'NONE' && (l.originalPacks ?? 0) > 0);
  const [choice, setChoice] = useState(() => Object.fromEntries(changed.map((l) => [l.id, firstChoice(l)])));
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showSame, setShowSame] = useState(false);
  const [openRows, toggleRow] = useOpenRows();
  const [branding, setBranding] = useState(null);
  useEffect(() => { brandingAPI.get().then(setBranding).catch(() => {}); }, []);

  const total = useMemo(() => review.lines.reduce((s, l) => {
    const packs = changeOf(l) === 'NONE' ? (l.originalPacks ?? 0) : finalPacks(l, choice[l.id]);
    return s + (l.packPrice != null ? packs * l.packPrice : 0);
  }, 0), [review.lines, choice]);
  const items = review.lines.filter((l) => (changeOf(l) === 'NONE' ? (l.originalPacks ?? 0) : finalPacks(l, choice[l.id])) > 0).length;
  const movedTaken = changed.filter((l) => changeOf(l) === 'MOVED' && choice[l.id] !== 'KEPT');
  const movingTo = [...new Set(movedTaken.map((l) => l.moveToVendorName))].join(', ');
  // Every item left moving to other suppliers: nothing is sent to this one, and it is closed.
  const allMoved = items === 0 && movedTaken.length > 0;
  const discontinuing = changed.filter((l) => choice[l.id] === 'DISCONTINUE').map((l) => l.name);
  const deferring = changed.filter((l) => choice[l.id] === 'DEFER');
  const discontinuingNote = (discontinuing.length ? ` ${discontinuing.join(', ')} will be marked discontinued.` : '')
    + deferring.map((l) => ` ${l.name} is left off orders ${l.deferUntil ? `until ${when(l.deferUntil)}` : `for ${plural(Number(l.deferDays), 'day')}`}.`).join('');
  // Nothing left to send, but items deferred or discontinued: those are done, and the order is closed.
  const closing = items === 0 && (deferring.length > 0 || discontinuing.length > 0);

  const accept = async () => {
    if (items === 0 && !allMoved && !closing) { notify('warning', 'Nothing would be left to order. Keep some of the lines, or drop the order.'); return; }
    const ok = await confirm(closing ? {
      title: `Close ${review.orderNumber}?`,
      message: `Nothing is left to send to ${review.vendorName}, so ${review.orderNumber} is closed without being sent.${movedTaken.length ? ` ${plural(movedTaken.length, 'item')} will go on the next order from ${movingTo}.` : ''}${discontinuingNote}`,
      confirmText: 'Accept and close',
    } : allMoved ? {
      title: `Move everything off ${review.orderNumber}?`,
      message: `Every item goes on the next order from ${movingTo}. Nothing is left for ${review.vendorName}, so ${review.orderNumber} is closed without being sent.${discontinuingNote}`,
      confirmText: 'Accept and move',
    } : {
      title: `Send ${review.orderNumber} to ${review.vendorName}?`,
      message: `${plural(items, 'item')}, about ${rs(total)}.${movedTaken.length ? ` ${plural(movedTaken.length, 'item')} will go on the next order from ${movingTo}.` : ''}${discontinuingNote} The order sheet opens to print, and the items count as on order from now on.`,
      confirmText: 'Accept and send',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await orderReviewAPI.accept(review.orderId, { decisions: choice, then: 'SEND' });
      if (res.order && !printOrderSheet(res.order, branding)) {
        notify('warning', 'The order was sent, but the browser blocked the print window. Allow pop-ups for this site, then open the review again and print it.');
      }
      announceMoves(notify, res.moved);
      announceDiscontinued(notify, res.discontinued);
      announceDeferred(notify, res.deferred);
      notify('success', res.order ? `${review.orderNumber} sent to ${review.vendorName}`
        : closing ? `${review.orderNumber} closed: nothing left to send to ${review.vendorName}`
          : `${review.orderNumber} closed: every item moved to another supplier`);
      window.dispatchEvent(new Event('clinic:orders-changed'));
      onDone();
    } catch (e) { notify('error', e.message || 'Could not send the order'); }
    finally { setBusy(false); }
  };

  const startSharing = () => {
    if (items === 0) { notify('warning', 'Nothing would be left on this order to share again. Keep some of the lines, or accept it.'); return; }
    setSharing(true);
  };

  const shareAgain = async (reviewerId, note, person) => {
    try {
      const next = await orderReviewAPI.accept(review.orderId, { decisions: choice, then: 'SHARE', reviewerId, note });
      setSharing(false);
      announceMoves(notify, next.moved);
      announceDiscontinued(notify, next.discontinued);
      announceDeferred(notify, next.deferred);
      notify('success', `${review.orderNumber} shared with ${person?.fullName || 'your colleague'}, with the changes you took`);
      window.dispatchEvent(new Event('clinic:orders-changed'));
      onChanged(next);
    } catch (e) { notify('error', e.message || 'Could not share it'); }
  };

  return (
    <div className="space-y-4">
      <ReviewHeader review={review}>
        <p className="mt-3 text-sm text-gray-700">
          {changed.length === 0
            ? `${r.reviewerName} changed nothing.`
            : `${r.reviewerName} made ${plural(changed.length, 'change')}. Take or keep each one, then accept and send.`}
          {r.status === 'TAKEN_BACK' && ' You took it back before they finished: these are the changes they had saved.'}
        </p>
      </ReviewHeader>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        {changed.length > 0 && (
          <ul className="divide-y divide-gray-100" aria-label="Changes">
            {changed.map((l) => {
              const c = changeOf(l);
              const look = lookOf(l, c);
              const picked = choice[l.id];
              return (
                <li key={l.id} className={`border-l-4 ${look.bar} px-4 py-3`}>
                  <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                    <div className="min-w-0 w-full sm:w-auto sm:flex-1">
                      <p className="font-semibold text-gray-900">{l.name} <span className={`ml-1 align-middle inline-block px-2 py-0.5 rounded-full text-xs font-medium ${look.chip}`}>{look.label}</span></p>
                      <p className="text-sm text-gray-800 mt-0.5">{changeSentence(l)}</p>
                      {addedLater(l) && <p className="text-xs text-violet-800 mt-0.5">{addedLater(l)}</p>}
                      <p className="text-xs text-gray-500 mt-0.5">{lineFacts(l, review.you.seesPrices !== false)}</p>
                      {c === 'MOVED' && review.you.seesPrices !== false && <p className="text-xs text-sky-800 mt-0.5">{priceThere(l)}</p>}
                      {l.reason && <p className="text-sm text-navy-900 mt-1">“{l.reason}” <span className="text-gray-500">({r.reviewerName})</span></p>}
                      {picked === 'DISCONTINUE' && (
                        <p className="text-xs text-gray-700 mt-1">
                          Marked discontinued when you accept: no order proposes it until it is resumed on the Medicines page, and it can no
                          longer be prescribed{Number(l.onHand) > 0 ? `, though ${Number(l.onHand).toLocaleString()} are in stock` : ''}.
                        </p>
                      )}
                      {picked === 'DEFER' && (
                        <p className={`text-xs mt-1 ${l.deferOnHand != null && Number(l.deferOnHand) <= 0 ? 'text-amber-800' : 'text-gray-700'}`}>{deferSentence(l)}</p>
                      )}
                      <div className="mt-1"><Why text={l.explanation} /></div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0 ml-auto">
                      <div role="radiogroup" aria-label={`${l.name}: take or keep`} className="flex gap-1 bg-gray-100 rounded-xl p-1 flex-wrap justify-end">
                        {choicesOf(l, c).map(([value, label]) => (
                          <button key={value} type="button" role="radio" aria-checked={picked === value}
                            onClick={() => setChoice((p) => ({ ...p, [l.id]: value }))}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${picked === value ? (value !== 'KEPT' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-900 shadow-sm') : 'text-gray-600 hover:bg-white/60'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                      {c === 'MOVED' && picked !== 'KEPT' ? (
                        <p className="text-sm text-sky-800 text-right">
                          To {l.moveToVendorName}
                          {l.movePackPrice != null && <span className="block font-semibold">about {rs((Number(l.proposedPacks) || 0) * l.movePackPrice)} there</span>}
                        </p>
                      ) : (
                        <p className="text-sm font-semibold text-gray-900">{l.packPrice != null ? rs(finalPacks(l, picked) * l.packPrice) : ''}</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {unchanged.length > 0 && (
          <div className={changed.length ? 'border-t border-gray-100' : ''}>
            <button type="button" onClick={() => setShowSame((v) => !v)} aria-expanded={showSame}
              className="w-full flex items-center justify-between px-5 py-3 text-sm text-gray-600 hover:bg-gray-50">
              <span>{plural(unchanged.length, 'line')} unchanged</span>
              {showSame ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showSame && (
              <ul className="divide-y divide-gray-100 border-t border-gray-100">
                {unchanged.map((l) => (
                  <li key={l.id}>
                    <button type="button" aria-expanded={openRows.has(l.id)} onClick={() => toggleRow(l.id)}
                      className="w-full text-left px-5 py-2 flex items-center justify-between gap-3 text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500">
                      <span className="text-gray-800">{l.name}{addedLater(l) && <span className="text-xs text-violet-800"> · {addedLater(l)}</span>}</span>
                      <span className="flex items-center gap-2 text-gray-600">
                        {packsText(l.originalPacks, l)}{l.packPrice != null ? ` · ${rs(l.originalPacks * l.packPrice)}` : ''}
                        {openRows.has(l.id) ? <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" /> : <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />}
                      </span>
                    </button>
                    {openRows.has(l.id) && <div className="px-5 pb-3"><LineStory l={l} review={review} /></div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 rounded-b-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-gray-600">{plural(items, 'item')} to send{movedTaken.length ? ` · ${movedTaken.length} moving to other suppliers` : ''}{Math.round(total) !== Math.round(review.totalBefore) ? ` · raised at ${rs(review.totalBefore)}` : ''}</p>
            <p className="text-2xl font-bold text-gray-900">{rs(total)}</p>
            {review.minimumOrderValue != null && total > 0 && total < review.minimumOrderValue && (
              <p className="text-xs text-amber-700">Below this supplier's minimum order of {rs(review.minimumOrderValue)}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DiscardButton review={review} prompt={prompt} notify={notify} onDone={onDone} busy={busy} />
            <button type="button" onClick={startSharing} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
              <UserCheck className="w-4 h-4" /> Share again
            </button>
            <button type="button" onClick={accept} disabled={busy}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
              {closing ? <CheckCircle2 className="w-4 h-4" /> : allMoved ? <Truck className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {' '}{closing ? 'Accept and close' : allMoved ? 'Accept and move' : 'Accept and send'}
            </button>
          </div>
        </div>
      </div>

      <History review={review} />

      {sharing && (
        <ShareForReviewDialog
          title={`Share ${review.orderNumber} again`}
          subtitle="The changes you took go on the order first; the next reviewer starts from there."
          onClose={() => setSharing(false)}
          onShare={shareAgain}
        />
      )}
    </div>
  );
};

const DiscardButton = ({ review, prompt, notify, onDone, busy }) => {
  const discard = async () => {
    const reason = await prompt({
      title: `Drop ${review.orderNumber}?`,
      subtitle: 'It was never sent. It stays in the orders list as cancelled, with your reason.',
      placeholder: 'e.g. Ordering from another supplier this week',
      multiline: true,
      required: true,
      errorMessage: 'Say why the order is being dropped',
      confirmText: 'Drop the order',
      cancelText: 'Back',
      tone: 'danger',
    });
    if (reason == null) return;
    try {
      await orderReviewAPI.discard(review.orderId, reason);
      notify('success', `${review.orderNumber} dropped`);
      window.dispatchEvent(new Event('clinic:orders-changed'));
      onDone();
    } catch (e) { notify('error', e.message || 'Could not drop the order'); }
  };
  return (
    <button type="button" onClick={discard} disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">
      <XCircle className="w-4 h-4" /> Drop the order
    </button>
  );
};

// =================================================================== looking only

const ReviewReadOnly = ({ review, seesOrders, onChanged, onDone }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const r = review.review;
  const changed = review.lines.filter((l) => changeOf(l) !== 'NONE');
  const waitingOnReviewer = r.status === 'WITH_REVIEWER';
  // Still with the reviewer: anyone else sees the order as raised, not their changes so far.
  const hidden = waitingOnReviewer && !review.you.reviewer;
  // Sent to the supplier and still standing; a reviewer is never handed the order, which has prices.
  const canPrint = r.status === 'ACCEPTED' && review.orderStatus !== 'CANCELLED' && review.you.seesPrices === true && seesOrders !== false;
  const [printing, setPrinting] = useState(false);
  const [openRows, toggleRow] = useOpenRows();
  const [branding, setBranding] = useState(null);
  useEffect(() => { if (canPrint) brandingAPI.get().then(setBranding).catch(() => {}); }, [canPrint]);
  // Loaded ahead for WhatsApp, which has to open its tab the moment it is clicked. Parked for now
  // (FEATURES.whatsappShare); Download PDF stands in for it.
  const [order, setOrder] = useState(null);
  const [sharing, setSharing] = useState(false);
  useEffect(() => {
    if (canPrint && FEATURES.whatsappShare) medicineOrderAPI.getById(review.orderId).then(setOrder).catch(() => {});
  }, [canPrint, review.orderId]);
  const [downloading, download] = usePdfDownload();
  // Like Print, the order as it stands now.
  const savePdf = () => download(async () => saveOrderPdf(await medicineOrderAPI.getById(review.orderId), branding));
  const share = async () => {
    setSharing(true);
    try { await shareOrderOnWhatsApp(order, branding, notify); } finally { setSharing(false); }
  };

  // The order as it stands now, not as the review left it: a line taken off since is not printed.
  const print = async () => {
    setPrinting(true);
    try {
      const order = await medicineOrderAPI.getById(review.orderId);
      if (!printOrderSheet(order, branding)) notify('warning', 'The browser blocked the print window. Allow pop-ups for this site and try again.');
    } catch (e) { notify('error', e.message || 'Could not open the order to print'); }
    finally { setPrinting(false); }
  };

  const takeBack = async () => {
    const ok = await confirm({
      title: `Take ${review.orderNumber} back from ${r.reviewerName}?`,
      message: `Whatever ${r.reviewerName} has saved comes back with it, and they can no longer change it.`,
      confirmText: 'Take it back',
    });
    if (!ok) return;
    try {
      onChanged(await orderReviewAPI.takeBack(review.orderId));
      notify('success', 'Taken back. Decide it here.');
    } catch (e) { notify('error', e.message || 'Could not take it back'); }
  };

  const status = {
    WITH_REVIEWER: review.you.reviewer ? '' : `With ${r.reviewerName}. It comes back here when they send it.`,
    RETURNED: `Back with ${r.raisedByName} to decide.`,
    TAKEN_BACK: `${r.raisedByName} took it back to decide.`,
    ACCEPTED: `Accepted by ${r.decidedBy} and sent to ${review.vendorName}.`,
    SHARED_AGAIN: `${r.decidedBy} took the changes they wanted and shared it again.`,
    DISCARDED: `${r.decidedBy} dropped the order.`,
    MOVED_AWAY: `${r.decidedBy} moved every item to other suppliers' next orders. Nothing was sent to ${review.vendorName}.`,
    NOTHING_LEFT: `${r.decidedBy} took the changes, and nothing was left to send. Nothing was sent to ${review.vendorName}.`,
    CANCELLED: 'The order was cancelled.',
    CLOSED: 'The order has moved on.',
  }[r.status] || '';

  return (
    <div className="space-y-4">
      <ReviewHeader review={review}>
        {status && <p className="mt-3 text-sm text-gray-700">{status}</p>}
        {(review.you.canTakeBack || review.you.canDiscard || canPrint) && (
          <div className="mt-3 flex gap-2 flex-wrap">
            {canPrint && (
              <button type="button" onClick={print} disabled={printing} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                <Printer className="w-4 h-4" /> {printing ? 'Opening…' : 'Print order'}
              </button>
            )}
            {canPrint && (
              <button type="button" onClick={savePdf} disabled={downloading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50">
                <FileDown className="w-4 h-4" /> {downloading ? 'Making the PDF…' : 'Download PDF'}
              </button>
            )}
            {canPrint && FEATURES.whatsappShare && (
              <button type="button" onClick={share} disabled={!order || sharing} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-emerald-300 bg-white text-sm font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-50">
                <MessageCircle className="w-4 h-4" /> {sharing ? 'Making the PDF…' : 'Share on WhatsApp'}
              </button>
            )}
            {review.you.canTakeBack && (
              <button type="button" onClick={takeBack} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50">
                <Undo2 className="w-4 h-4" /> Take it back
              </button>
            )}
            {review.you.canDiscard && <DiscardButton review={review} prompt={prompt} notify={notify} onDone={onDone} />}
          </div>
        )}
      </ReviewHeader>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <p className="px-5 pt-4 font-semibold text-gray-800">
          {waitingOnReviewer ? 'The order as raised' : changed.length ? plural(changed.length, 'change') : 'No changes'}
        </p>
        <ul className="divide-y divide-gray-100 mt-2">
          {(hidden ? review.lines.filter((l) => l.originalPacks != null) : review.lines).map((l) => {
            const c = hidden ? 'NONE' : changeOf(l);
            const look = lookOf(l, c);
            const open = openRows.has(l.id);
            return (
              <li key={l.id} className={`border-l-4 ${look.bar}`}>
                <button type="button" aria-expanded={open} onClick={() => toggleRow(l.id)}
                  className="w-full text-left px-5 py-3 flex items-start justify-between gap-3 flex-wrap hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{l.name} {look.label && <span className={`ml-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${look.chip}`}>{look.label}</span>}</p>
                    <p className="text-sm text-gray-700">{c === 'NONE' ? packsText(l.originalPacks ?? l.proposedPacks, l) : changeSentence(l)}</p>
                    {c !== 'NONE' && l.reason && <p className="text-sm text-navy-900">“{l.reason}”</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {l.decision && (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${l.decision === 'TAKEN' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                        {l.decision !== 'TAKEN' ? 'Kept as raised' : l.movedToOrderNumber ? `Moved to ${l.movedToOrderNumber}` : l.markedDiscontinued ? 'Marked discontinued' : l.deferredUntil ? `Deferred to ${when(l.deferredUntil)}` : 'Taken'}
                      </span>
                    )}
                    {open ? <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" /> : <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />}
                  </div>
                </button>
                {open && <div className="px-5 pb-4"><LineStory l={l} review={review} hidden={hidden} /></div>}
              </li>
            );
          })}
        </ul>
        {hidden && (
          <p className="px-5 py-3 text-xs text-gray-500 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Not sent: the supplier has seen nothing yet.</p>
        )}
      </div>

      <History review={review} />
    </div>
  );
};

/** Earlier rounds of the same order. */
const History = ({ review }) => {
  if (!review.history || review.history.length === 0) return null;
  return (
    <section aria-label="Earlier rounds" className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <p className="font-semibold text-gray-800">Earlier rounds</p>
      <ul className="mt-2 space-y-1 text-sm text-gray-700">
        {review.history.map((h) => (
          <li key={h.id}>
            Round {h.round}: {h.reviewerName}, {h.changes ? plural(h.changes, 'change') : 'no changes'}
            {h.changes ? ` (${h.taken} taken, ${h.kept} kept)` : ''}, {(STATE[h.status] || STATE.CLOSED).label.toLowerCase()} {when(h.decidedAt || h.returnedAt || h.sharedAt)}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default OrderReviewsManagement;
